import './styles.scss';

import {
  AccidentIssueAnyoneInjuredEnum,
  AccidentIssueMedicalNeededEnum,
  AccidentIssueSafetyNotifiedEnum,
  AccidentIssueWasCeoCalledEnum,
  AccidentIssueWasCrewInvolvedEnum,
  AccidentIssueWithCacheCanVehicleBeDrivenEnum,
  AccidentIssueWithCacheDidDriverCallHelpEnum,
  AccidentIssueWithCacheWasDriverAtFaultEnum,
  AccidentIssueWithCacheWasThirdPartyInvolvedEnum,
} from '@/models/gen/graphql';
import { Button, Col, Row } from 'react-bootstrap';
import { Datetime, createNotification } from '@/utils';
import { DispatcherDropdownField, DriverDropdownField, UserDropdownField } from '@/components/UserDropdown';
import { EnumDropdownField, getItemsFromEnum } from '@/components/EnumDropdown';
import GenericFileUpload, { GenericFile, GenericFileType } from '@/components/FileUpload/new';
import { useMemo, useRef, useState } from 'react';

import AccidentFormHistoryTable from '@/features/AccidentHistory/components/AccidentHistoryForm/AccidentFormHistoryTable';
import { AccidentHistoryFormDefinitions } from '@/features/AccidentHistory/components/AccidentHistoryForm/types';
import { AirportDropdownField } from '@/components/AirportDropdown';
import { AssignVehicleDropdownField } from '@/components/VehicleDropdown';
import { DATETIME_FORMAT } from '@/constants';
import { DatetimeInputField } from '@/components/DatetimeInput';
import Field from '@/components/Field';
import { FlightNumberOrLoopNameInputField } from '@/common/FlightNumberInput';
import Form from '@/common/Form';
import { InputField } from '@/components/Input';
import { LoadingBlur } from '@/components/LoadingSpinner';
import { TextAreaField } from '@/components/TextArea';
import { Toast } from '@/models';
import { createAccidentIssue } from '@/features/AccidentHistory/api/services/createAccidentIssue';
import { createAccidentIssueDocumentPresignedUrl } from '../../api/services/createAccidentIssueDocumentPresignedUrlEndpoint';
import { deleteAccidentIssueDocument } from '../../api/services/deleteAccidentIssueDocument';
import { updateAccidentIssue } from '@/features/AccidentHistory/api/services/updateAccidentIssue';
import { useAccidentHistory } from '@/features/AccidentHistory';
import { useAccidentHistoryDrawer } from '../AccidentHistoryDrawer';
import useConfirmation from '@/hooks/useConfirmation';
import useLocale from '@/hooks/useLocale';
import { useNavigate } from 'react-router-dom';
import DeferredFileUpload, { DeferredFileUploadHandle } from '@/components/FileUpload/deferred-file-upload';
import { recreateAccidentIssueDocumentDownloadUrl } from '@/features/AccidentHistory/api/services/createAccidentIssueDocumentDownloadURL';
import { useServiceIssueFilters } from '@/hooks/useServiceIssueFilters';

const AccidentHistoryForm = ({
  onSubmit,
  onEditAccident,
  locale: customLocale,
  value = {},
  trip,
}: AccidentHistoryFormDefinitions.Props): JSX.Element => {
  const [, setState, { submitFilters }] = useAccidentHistory();
  const [loading, setLoading] = useState(false);
  const setAccidentHistoryDrawer = useAccidentHistoryDrawer(({ setState }) => setState);
  const [documents, setDocuments] = useState(value.documents ?? []);
  const locale = useLocale({
    ...AccidentHistoryFormDefinitions.getLocale(value),
    ...customLocale,
  });
  const fileUploadRef = useRef<DeferredFileUploadHandle>(null);
  const confirmDocumentDelete = useConfirmation();
  const navigate = useNavigate();
  const handleDeleteDocument = async (id: string) => {
    await confirmDocumentDelete()
      .then(async () => {
        try {
          await deleteAccidentIssueDocument([{ id }]);
          createNotification('Sucessfully Deleted Document', Toast.Type.SUCCESS, 'Accident Issue');
          value.documents = value.documents.filter((document) => document.id !== id);
          setDocuments((current) => current.filter((document) => document.id !== id));
          await submitFilters?.(useServiceIssueFilters.getState().state); // Because we aren't resubmitting on closing the form
        } catch (e) {
          console.error('Error deleting document', e);
          createNotification('Could Not Delete Document', Toast.Type.DANGER, 'Accident Issue');
        }
      })
      .catch(() => console.log('Cancelled deleting document'));
  };
  const handleSubmit = async (data) => {
    try {
      setLoading(true);
      if (data.id) {
        await updateAccidentIssue([data]);
        createNotification('Sucessfully Updated Accident', Toast.Type.SUCCESS, 'Accident Issue');
        setState((current) => ({
          ...current,
          accidents: current.accidents.set(data.id, {
            ...current.accidents.get(data.id),
            ...data,
          }),
        }));
      } else {
        const filesToUpload = fileUploadRef.current?.getFilesToUpload() ?? [];
        const { id, ...create } = data;
        const createRes = await createAccidentIssue([
          { ...create, documents: filesToUpload.map((file) => ({ ...file, issueId: '', fileLocation: '' })) },
        ]);
        createNotification('Sucessfully Created Accident', Toast.Type.SUCCESS, 'Accident Issue');
        await fileUploadRef.current?.performDeferredUploads(createRes?.[0]?.output?.document ?? []); // No bulk create, so we only get back one issue
      }
    } catch (err) {
      console.error('Error Creating Lost and Found.', err);
      createNotification('Could Not Create Accident', Toast.Type.DANGER, 'Accident Issue');
    } finally {
      await submitFilters?.(useServiceIssueFilters.getState().state);
      setLoading(false);
      onSubmit?.(data);
    }
  };
  const gotoTrips = () => {
    navigate('/trips');
    setAccidentHistoryDrawer((current) => ({
      ...current,
      show: false,
    }));
  };
  const fullValue = useMemo(
    () => ({
      ...AccidentHistoryFormDefinitions.initValue,
      ...value,
      ...AccidentHistoryFormDefinitions.loadForm(),
    }),
    []
  );

  return (
    <Form
      name="AccidentHistoryForm"
      className="AccidentHistoryForm"
      onSubmit={handleSubmit}
      value={fullValue}
      validator={AccidentHistoryFormDefinitions.validator(locale)}
      onChange={AccidentHistoryFormDefinitions.saveForm}
    >
      <LoadingBlur loading={loading} />
      <Form.Input name="id" type="hidden" />
      <Form.Input name="tripId" type="hidden" />
      <Form.Input name="stationText" type="hidden" />
      <Row className="AccidentHistoryFormHeader">
        <Col>
          {value.tripId && (
            <>
              <span>
                {value.tripServicerIataAirlineCode}
                {value.tripLoopName || value.tripFlightNumber.toString().padStart(4, '0')}{' '}
                {new Datetime(value.tripScheduled).format(DATETIME_FORMAT)}
              </span>
              <span>
                {value.stationText} - {[value.driverFirstName, value.driverLastName].join(' ')} - #{value.vehicleTrackingId}
              </span>
            </>
          )}
          <h4 className="AccidentHistoryFormTitle">{locale('Accident')}</h4>
        </Col>
      </Row>
      {!value.tripId && !value?.id && (
        <Row className="AccidentHistoryFormLinkTrips">
          <Col>
            <span>THIS IS NOT LINKED TO TRIP.</span>
            <span>
              WOULD YOU LIKE TO LINK THIS TO A TRIP?{' '}
              <Button variant="text" onClick={gotoTrips}>
                YES
              </Button>
            </span>
          </Col>
        </Row>
      )}
      <Row>
        <Col>
          <Form.Input as={TextAreaField} name="description" label={locale('Description of Accident')} validate />
        </Col>
      </Row>
      <Form.Watch name="stationText">
        {([station]: [string]) => (
          <Row>
            <Col xs={6}>
              <Form.Input as={DriverDropdownField} airports={station} name="driverId" label={locale('Driver Name')} validate />
            </Col>
            <Col xs={6}>
              <Form.Input
                as={AssignVehicleDropdownField}
                name="vehicleId"
                label={locale('Van Number')}
                airportCode={station}
                disabled={!station}
                validate
              />
            </Col>
          </Row>
        )}
      </Form.Watch>
      <Row>
        {!value.tripId && (
          <Col xs={6}>
            <Form.Input as={AirportDropdownField} name="stationText" label={locale('Station')} validate />
          </Col>
        )}
        <Col xs={{ span: 6, offset: value.tripId ? 3 : 0 }}>
          <Form.Input as={DatetimeInputField} name="accidentDatetime" label={locale('Accident Date/Time')} validate />
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <Form.Input
            as={EnumDropdownField}
            name="anyoneInjured"
            label={locale('Anyone Injured?')}
            enum={getItemsFromEnum(AccidentIssueAnyoneInjuredEnum)}
            validate
          />
        </Col>
        <Col xs={6}>
          <Form.Input
            as={EnumDropdownField}
            name="medicalNeeded"
            label={locale('Medical Attention Needed?')}
            enum={getItemsFromEnum(AccidentIssueMedicalNeededEnum)}
            validate
          />
        </Col>
      </Row>
      <Form.Watch name="anyoneInjured">
        {([wasAnyoneInjured]: [AccidentIssueAnyoneInjuredEnum]) =>
          wasAnyoneInjured === AccidentIssueAnyoneInjuredEnum.Yes && (
            <Row>
              <Col>
                <Form.Input
                  as={TextAreaField}
                  name="typeOfMedicalAttention"
                  label={locale('What Type Of Medical Attention Was Offered/Required?')}
                  placeholder={locale('Treated on scene, which hospital were they sent to, etc')}
                  validate={wasAnyoneInjured === AccidentIssueAnyoneInjuredEnum.Yes}
                />
              </Col>
            </Row>
          )
        }
      </Form.Watch>
      <Row>
        <Col>
          <Form.Input
            as={InputField}
            name="specificLocation"
            label={locale('Specific Location Of The Accident')}
            maxLength={512}
            validate
          />
        </Col>
      </Row>
      <Field className="AccidentHistoryFormCheckboxHeader" label={locale('Check All That Apply:')}>
        <Form.Checkbox name="additionalDriverScheduled" label={locale('Arranged another driver to transport crew members (if needed)')} />
        <Form.Checkbox name="driverContactedManager" label={locale('Driver contacted manager')} />
        <Form.Checkbox name="dispatcherContactedManager" label={locale('You contacted manager')} />
      </Field>
      <Row>
        <Col>
          <Form.Input
            as={EnumDropdownField}
            name="wasCrewInvolved"
            label={locale('Were Crew Onboard?')}
            enum={getItemsFromEnum(AccidentIssueWasCrewInvolvedEnum)}
            validate
          />
        </Col>
      </Row>
      <Form.Watch name="wasCrewInvolved">
        {([wasCrewInvolved]: [AccidentIssueWasCrewInvolvedEnum]) =>
          wasCrewInvolved === AccidentIssueWasCrewInvolvedEnum.Yes && (
            <Row>
              <Col>
                <Form.Input
                  as={TextAreaField}
                  name="infoOfCrewMembers"
                  label={locale('Information Of All Crew Members')}
                  placeholder={locale('Name and phone numbers')}
                  validate={wasCrewInvolved === AccidentIssueWasCrewInvolvedEnum.Yes}
                />
              </Col>
            </Row>
          )
        }
      </Form.Watch>
      <Row>
        <Col xs={6}>
          <Form.Input
            as={EnumDropdownField}
            name="didDriverCallHelp"
            label={locale('Did Driver Call 911?')}
            enum={getItemsFromEnum(AccidentIssueWithCacheDidDriverCallHelpEnum)}
            validate
          />
        </Col>
        <Col xs={6}>
          <Form.Input
            as={EnumDropdownField}
            name="canVehicleBeDriven"
            label={locale('Can Vehicle Be Driven?')}
            enum={getItemsFromEnum(AccidentIssueWithCacheCanVehicleBeDrivenEnum)}
            validate
          />
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <Form.Input
            as={EnumDropdownField}
            name="wasDriverAtFault"
            label={locale('Was Driver At Fault?')}
            enum={getItemsFromEnum(AccidentIssueWithCacheWasDriverAtFaultEnum)}
            validate
          />
        </Col>
        <Col xs={6}>
          <Form.Input
            as={EnumDropdownField}
            name="wasCeoCalled"
            label={locale('Was CEO Called?')}
            enum={getItemsFromEnum(AccidentIssueWasCeoCalledEnum)}
            validate
          />
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <Form.Input as={UserDropdownField} name="safetyId" label={locale('Who Is Safety On Call?')} validate />
        </Col>
        <Col xs={6}>
          <Form.Input
            as={EnumDropdownField}
            name="safetyNotified"
            label={locale('Was Safety Notified?')}
            enum={getItemsFromEnum(AccidentIssueSafetyNotifiedEnum)}
            validate
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Input
            as={EnumDropdownField}
            name="wasThirdPartyInvolved"
            label={locale('Was 3rd Party Involved?')}
            enum={getItemsFromEnum(AccidentIssueWithCacheWasThirdPartyInvolvedEnum)}
            validate
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Watch name="wasThirdPartyInvolved">
            {([wasThirdPartyInvolved]: [AccidentIssueWithCacheWasThirdPartyInvolvedEnum]) => (
              <Form.Input
                as={TextAreaField}
                name="thirdPartyInformation"
                label={locale('3rd Party Information')}
                validate={wasThirdPartyInvolved === AccidentIssueWithCacheWasThirdPartyInvolvedEnum.Yes}
              />
            )}
          </Form.Watch>
        </Col>
      </Row>
      <Form.Watch name="anyoneInjured">
        {([anyoneInjured]: [AccidentIssueAnyoneInjuredEnum]) =>
          anyoneInjured === AccidentIssueAnyoneInjuredEnum.Yes && (
            <Row>
              <Col>
                <Form.Input
                  as={TextAreaField}
                  name="infoOfInjuredParties"
                  label={locale('Information Of All Injured Parties')}
                  validate={anyoneInjured === AccidentIssueAnyoneInjuredEnum.Yes}
                />
              </Col>
            </Row>
          )
        }
      </Form.Watch>
      <Row>
        <Col>
          <Form.Watch name="wasThirdPartyInvolved">
            {([val]: AccidentIssueWithCacheWasThirdPartyInvolvedEnum[]) => (
              <Form.Input
                as={TextAreaField}
                name="damageToOtherVehicles"
                label={locale('Describe Damage To Other Vehicles')}
                validate={val === AccidentIssueWithCacheWasThirdPartyInvolvedEnum.Yes}
              />
            )}
          </Form.Watch>
        </Col>
      </Row>
      <Row className="AccidentHistoryFormSectionHeader">
        <Col>
          <h4 className="AccidentHistoryFormCommentsTitle">{locale('RECOVERY INFORMATION')}</h4>
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <Form.Input as={FlightNumberOrLoopNameInputField} name="outboundFlightName" label={locale('Outbound FLT #')} />
        </Col>
        <Col xs={6}>
          <Form.Input as={DatetimeInputField} name="outboundDatetime" label={locale('Outbound Date/Time')} />
        </Col>
      </Row>
      <Row>
        <Col xs={{ span: 6, offset: 3 }}>
          <Form.Input as={DispatcherDropdownField} name="dispatcherId" label={locale('Dispatcher Name')} validate />
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Button name="SUBMIT" type="submit" variant={locale('SUBMIT_BUTTON_COLOR')}>
            {locale('SUBMIT_BUTTON_TEXT')}
          </Form.Button>
        </Col>
      </Row>
      {value.id && (
        <>
          <Row className="AccidentHistoryFormUpload">
            <Col>
              <GenericFileUpload
                endpoint={async (file: GenericFileType) => {
                  try {
                    const payload = {
                      filename: file.file.name,
                      md5: file.md5,
                      fileSize: file.file.size,
                      issueId: value.id,
                    };
                    const res = await createAccidentIssueDocumentPresignedUrl(payload);
                    createNotification('Sucessfully Uploaded Document', Toast.Type.SUCCESS, 'Accident Issue');
                    return [res?.url, res?.contentType];
                  } catch (e) {
                    console.error('Error Uploading Document', e);
                    createNotification('Could Not Upload Document', Toast.Type.DANGER, 'Accident Issue');
                  }
                }}
                options={{
                  showFiles: true,
                  multiple: true,
                }}
                locale={{
                  Upload: 'UPLOAD FILES',
                }}
                onError={async (...args) => console.log('', ...args)}
                onSuccess={async (...args) => {
                  await submitFilters?.(useServiceIssueFilters.getState().state);
                  const documents = await recreateAccidentIssueDocumentDownloadUrl({ issueId: [value.id] });
                  const res = documents.urls.map((document) => {
                    const md5Match = document.url.match(/accident\/([a-f0-9]{32})\//); // Extract md5 from URL
                    const md5 = md5Match ? md5Match[1] : null;
                    return {
                      md5,
                      url: document.url,
                    };
                  });
                  return res;
                }}
              />
            </Col>
          </Row>
          {documents?.map((document) => (
            <Row className="pb-2" key={document.id}>
              <Col>
                <GenericFile name={document.filename} preview={document.fileLocation} onDelete={() => handleDeleteDocument(document.id)} />
              </Col>
            </Row>
          ))}
        </>
      )}
      {!value.id && (
        <>
          <Row className="AccidentHistoryFormUpload">
            <Col>
              <DeferredFileUpload ref={fileUploadRef} options={{ multiple: true, showFiles: true }} />
            </Col>
          </Row>
        </>
      )}
      {value.tripId && (
        <>
          <Row className="AccidentHistoryFormHistory">
            <Col>
              <h4 className="AccidentHistoryFormHistoryTitle">{locale('Accident History')}</h4>
            </Col>
          </Row>
          <Row>
            <Col>
              <AccidentFormHistoryTable
                onEditAccident={onEditAccident}
                id={value.tripId}
                driverFirstName={trip?.driver?.firstName}
                driverLastName={trip?.driver?.lastName}
                vehicleTrackingId={trip?.vehicle?.trackingId}
              />
            </Col>
          </Row>
        </>
      )}
    </Form>
  );
};

export default AccidentHistoryForm;
